// §11.10 — "Tray flyout plus a window": the flyout half.
//
// It sits directly in `skins/` beside `notifications.tsx` for the same reason that one does — a
// skin may not import another skin, but a leaf shared by all of them is not a skin (§6.5). The
// month arithmetic comes from `@hangar/shared`, which is the *only* module the Clock & Calendar
// app and a skin can both reach, so the flyout and the window cannot disagree about what April
// looks like.
import { useEffect, useMemo, useState } from 'react';
import { addMonths, byDay, dayKeyOf, monthGrid } from '@hangar/shared';
import { COPY } from '@/copy';
import { CLOCK_TICK_MS } from '@/config';
import { useKernel, useNotifications } from '@/kernel/react';

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'] as const;

export function TrayCalendar() {
  const kernel = useKernel();
  const { center } = useNotifications();
  const [now, setNow] = useState(() => Date.now());
  const [view, setView] = useState(() => {
    const date = new Date();
    return { year: date.getFullYear(), month: date.getMonth() };
  });

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), CLOCK_TICK_MS);
    return () => clearInterval(timer);
  }, []);

  const today = new Date(now);
  const todayKey = dayKeyOf(today.getFullYear(), today.getMonth(), today.getDate());
  const grid = useMemo(() => monthGrid(view.year, view.month), [view]);
  const marked = useMemo(() => byDay(center), [center]);

  return (
    <div className="tray-calendar" data-testid="tray-calendar">
      <button
        type="button"
        className="tray-calendar-now"
        data-testid="tray-calendar-open"
        title={COPY.clock.open}
        onClick={() => kernel.launch.launch('clock')}
      >
        <span className="tray-calendar-time" data-testid="tray-calendar-time">
          {today.toLocaleTimeString()}
        </span>
        <span className="tray-calendar-date">
          {today.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })}
        </span>
      </button>

      <div className="tray-calendar-month">
        <button
          type="button"
          aria-label={COPY.clock.previousMonth}
          data-testid="tray-calendar-prev"
          onClick={() => setView((current) => addMonths(current.year, current.month, -1))}
        >
          ‹
        </button>
        <span data-testid="tray-calendar-month">
          {new Date(view.year, view.month, 1).toLocaleDateString(undefined, {
            month: 'long',
            year: 'numeric',
          })}
        </span>
        <button
          type="button"
          aria-label={COPY.clock.nextMonth}
          data-testid="tray-calendar-next"
          onClick={() => setView((current) => addMonths(current.year, current.month, 1))}
        >
          ›
        </button>
      </div>

      <div className="tray-calendar-grid" data-testid="tray-calendar-grid">
        {WEEKDAYS.map((day, index) => (
          <span key={index} className="tray-calendar-weekday">
            {day}
          </span>
        ))}
        {grid.map((cell) => (
          <span
            key={cell.key}
            className="tray-calendar-day"
            data-testid={`tray-calendar-day-${cell.key}`}
            data-in-month={String(cell.inMonth)}
            data-today={String(cell.key === todayKey)}
            data-notices={marked.get(cell.key)?.length ?? 0}
          >
            {cell.day}
          </span>
        ))}
      </div>
    </div>
  );
}
